<template>
    <div class="website-traffic-by-country-chart" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <DoubleBarChart
                :title="$t('report.websiteTrafficByCountry')"
                :xAxisData="getXAxisData()"
                :yAxisData="getYAxisData()"
                :series="getSeries()"
                v-if="!tableMode"
                :color-palette="[['#00BFFF', '#FFFFFF']]"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="$t('report.topVisitorCountry')" :value="formatStat(getStats().TopCountry)" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { mapState } from "vuex";
import fscreen from "fscreen";
import { REPORT_API } from "../../../api/statistics";

const log = log2("WebsiteTrafficByCountry");
export default Vue.extend({
    name: "WebsiteTrafficByCountry",
    components: { DoubleBarChart, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.sessionCount")],
            stats: {
                TopCountry: 0,
            },
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
            data: [],
            loading: true,
        };
    },
    computed: {},
    methods: {
        formatStat(value) {
            if (value === "-") {
                return this.$t("report.noEnoughtData");
            }
            return value;
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.websiteTrafficByCountry(this.filters)
                .then((res) => {
                    log("websiteTrafficByCountry data fetched", res);
                    this.data = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".website-traffic-by-country-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("message.country"),
                    dataIndex: "country",
                    key: "country",
                },
                {
                    title: this.$t("report.visitorCount"),
                    dataIndex: "newVisitorCount",
                    key: "newVisitorCount",
                },
            ];
        },
        getRows() {
            let items = this.data || [];

            log("getRows.items", items);
            const rows = items.map((item) => {
                return {
                    country: item.country,
                    newVisitorCount: item.newVisitorCount,
                };
            });
            return rows;
        },
        getStats() {
            let items = this.data || [];

            const TopCountry = _.chain(items).maxBy("newVisitorCount").value()?.country || "-";

            return {
                TopCountry,
            };
        },
        getYAxisData() {
            const countries = this.data || [];
            return {
                type: "category",

                data: countries.map((country) => {
                    return country.country;
                }),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                },
                splitLine: {
                    show: false,
                },
            };
        },
        getXAxisData() {
            const items = this.data || [];
            const max = _.max(items.map((x) => x.newVisitorCount || 0)) || 0;
            console.log("getXAxisData", {
                max,
                items,
            });

            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    rotate: -30,
                },
            };
        },
        getSeries() {
            const items = this.data || [];
            log("getSeries.items", items);

            const series = [
                {
                    colorBy: "data",
                    showBackground: true,
                    type: "bar",
                    label: {
                        position: "right",
                        show: true,
                    },
                    data: items.map((item) => item.newVisitorCount),
                },
            ];

            return series;
        },
        exportToExcel() {
            let items = this.data || [];

            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    Country: item.country || "-",
                    "Total Visitor Count": item.newVisitorCount,
                };
            });
            expertToExcel(excelData, `website-traffic-by-country.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".website-traffic-by-country-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
        this.fetch();
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.website-traffic-by-country-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.website-traffic-by-country-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
